import { INLINE_CORRECTIONS } from '../../../ReportCorrections/constants';
import { useMemo } from 'react';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { Editor, HistoryEditor, Path, Point, Range, withHistory } from '../../core';
import type { Operation } from '../../core';
import { reconcileHistory } from './actions';
import type { EditorPlugin } from '../../types';
import { getRenderLeaf } from '../../utils';
import { DictationCheckLeaf } from './components/DictationCheckLeaf';
import { decorate } from './decorate';
import { getDictationCheckStore } from './store';
import { DICTATION_CHECK_PLUGIN_ID } from './types';
import type { DictationCheckItem } from './types';
import { useDictationCheckEditable } from './useDictationCheckEditable';

export function DictationCheckPlugin(): EditorPlugin {
  return {
    pluginID: DICTATION_CHECK_PLUGIN_ID,
    decorate,
    enhanceEditable: useDictationCheckEditable,
    renderLeaf: getRenderLeaf({
      pluginID: DICTATION_CHECK_PLUGIN_ID,
      component: DictationCheckLeaf,
    }),
    enhanceEditorState: (editor) => {
      // Install history here if necessary; HistoryPlugin's enhancer is idempotent.
      if (!HistoryEditor.isHistoryEditor(editor)) withHistory(editor);
      const { apply, undo, redo } = editor;
      const store = getDictationCheckStore(editor);
      type Ranges = Map<DictationCheckItem, Range | null>;
      const historyRanges = new WeakMap<object, { before: Ranges; after: Ranges }>();
      const captureRanges = (): Ranges =>
        new Map(
          [...store.getSnapshot().items, ...store.getSnapshot().resolved].map((item) => [
            item,
            item.rangeRef.current,
          ])
        );
      store.setHistoryRecorder(() => {
        const batch = editor.history.undos[editor.history.undos.length - 1];
        const saved = batch && historyRanges.get(batch);
        if (saved) saved.after = captureRanges();
      });
      const replay = (direction: 'undo' | 'redo', action: () => void) => {
        store.withAction(() => {
          const batches = direction === 'undo' ? editor.history.undos : editor.history.redos;
          const batch = batches[batches.length - 1];
          if (!batch) return;
          const savedRanges = historyRanges.get(batch)?.[direction === 'undo' ? 'before' : 'after'];
          action();
          // Exact batch endpoints avoid adjacent ranges absorbing one another during replay.
          // Keep normal range refs for all edits between history operations.
          savedRanges?.forEach((range, item) => {
            const snapshot = store.getSnapshot();
            if (![...snapshot.items, ...snapshot.resolved].includes(item)) return;
            item.rangeRef.unref();
            if (range) item.rangeRef = Editor.rangeRef(editor, range, { affinity: 'inward' });
          });
          reconcileHistory(editor, direction);
        });
      };
      editor.undo = () => replay('undo', undo);
      editor.redo = () => replay('redo', redo);
      let applyDepth = 0;
      editor.apply = (operation: Operation) => {
        const before = captureRanges();
        const isTopLevel = applyDepth === 0;
        const touched =
          isTopLevel && !store.isApplyingAction()
            ? store
                .getSnapshot()
                .items.filter(
                  (item) =>
                    item.rangeRef.current && touches(editor, operation, item.rangeRef.current)
                )
            : [];
        touched.forEach((item) => {
          store.resolve(item.change.id, 'edited');
          item.rangeRef.affinity = 'outward';
        });
        applyDepth += 1;
        try {
          apply(operation);
        } finally {
          applyDepth -= 1;
        }
        const snapshot = store.getSnapshot();
        snapshot.resolved.forEach((item) => {
          if (store.isApplyingAction() || item.status !== 'edited' || !item.rangeRef.current)
            return;
          item.resolvedText = Editor.string(editor, item.rangeRef.current);
          item.rangeRef.affinity = Range.isCollapsed(item.rangeRef.current) ? 'outward' : 'inward';
        });
        if (
          isTopLevel &&
          operation.type !== 'set_selection' &&
          HistoryEditor.isSaving(editor) !== false
        ) {
          const batch = editor.history.undos[editor.history.undos.length - 1];
          if (batch) {
            const saved = historyRanges.get(batch);
            historyRanges.set(batch, { before: saved?.before ?? before, after: captureRanges() });
          }
        }
        if (operation.type !== 'set_selection' && !store.isApplyingAction()) store.update();
      };
      return editor;
    },
  };
}

/** Shared by the reporter plugin list; flag off installs no editor behavior. */
export function useDictationCheckPlugins(): EditorPlugin[] {
  const [isEnabled] = useFeatureFlagEnabled(FF.LLM_REPORT_POST_PROCESSING);
  return useMemo(
    () => (isEnabled && INLINE_CORRECTIONS ? [DictationCheckPlugin()] : []),
    [isEnabled]
  );
}

function touches(editor: Editor, operation: Operation, range: Range): boolean {
  if (operation.type === 'set_selection') return false;
  if (operation.type === 'insert_text' || operation.type === 'remove_text') {
    const start = { path: operation.path, offset: operation.offset };
    const end = {
      ...start,
      offset: start.offset + (operation.type === 'remove_text' ? operation.text.length : 0),
    };
    // Inserting at an endpoint is considered editing that mark, including a pure insertion mark.
    if (operation.type === 'insert_text') return Range.includes(range, start);
    const intersection = Range.intersection(range, { anchor: start, focus: end });
    return !!intersection && (!Range.isCollapsed(intersection) || Range.isCollapsed(range));
  }
  if (operation.type === 'split_node') {
    return Range.includes(range, { path: operation.path, offset: operation.position });
  }
  if (operation.type === 'insert_node') {
    return (
      Path.compare(operation.path, Range.start(range).path) > 0 &&
      Path.compare(operation.path, Range.end(range).path) <= 0
    );
  }
  if (operation.type === 'move_node') {
    return (
      Path.isAncestor(operation.path, range.anchor.path) ||
      Path.equals(operation.path, range.anchor.path)
    );
  }
  if (operation.type === 'merge_node') return false; // Normalization keeps refs on the same text.
  const nodeRange = Editor.range(editor, operation.path);
  return (
    Point.compare(Range.end(nodeRange), Range.start(range)) >= 0 &&
    Point.compare(Range.start(nodeRange), Range.end(range)) <= 0
  );
}
